import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/services/endpoints/auth';
import { useAuthStore } from '@/app/store/authStore';
import { ApiError } from '@/services/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';

export function ChangePasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success(t('auth.passwordChanged'));

      // The server revokes every session on a password change, so the only
      // correct next step is a fresh sign-in.
      await logout();
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('errors.unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
      >
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{t('auth.changePasswordTitle')}</h1>
          {user?.mustChangePassword && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {t('auth.mustChangePassword')}
            </p>
          )}
        </div>

        {error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <Input
          label={t('auth.currentPassword')}
          type="password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          autoComplete="current-password"
          required
        />

        <Input
          label={t('auth.newPassword')}
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          hint={t('auth.passwordRules')}
          autoComplete="new-password"
          required
        />

        <Input
          label={t('auth.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          error={mismatch ? t('auth.passwordsDoNotMatch') : undefined}
          autoComplete="new-password"
          required
        />

        <Button
          type="submit"
          fullWidth
          size="lg"
          isLoading={isSubmitting}
          disabled={!currentPassword || newPassword.length < 8 || mismatch}
        >
          {t('auth.changePassword')}
        </Button>
      </form>
    </div>
  );
}
